import { useState } from "react";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  ["PriceStrike", "#product"],
  ["How it works", "#how"],
  ["Evidence", "#evidence"],
  ["Pricing", "#pricing"],
  ["Platform", "#platform"],
  ["Investors", "#investors"],
  ["Design partners", "#pilot"],
];

export default function Navbar() {
  const lenis = useLenis();
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    lenis?.scrollTo(id, { offset: -72, duration: 1.4 });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-2xl"
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 lg:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => go(0)}
          className="font-display text-lg font-extrabold tracking-tighter text-white"
        >
          AXIOSERA<span className="blink" />
        </button>
        <nav className="hidden items-center gap-6 xl:flex">
          {LINKS.map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-link-${label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => go(id)}
              className="group font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-[#00FF41]"
            >
              <span className="text-[#00FF41] opacity-0 transition-opacity duration-300 group-hover:opacity-100">[ </span>
              {label}
              <span className="text-[#00FF41] opacity-0 transition-opacity duration-300 group-hover:opacity-100"> ]</span>
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-join-pilot-button"
            onClick={() => go("#pilot")}
            className="hidden border border-[#00FF41] px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#00FF41] transition-colors duration-300 hover:bg-[#00FF41] hover:text-black sm:block"
          >
            Join the pilot
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            className="border border-white/20 p-2 text-white transition-colors duration-300 hover:border-[#00FF41] hover:text-[#00FF41] xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-2xl xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6">
              {LINKS.map(([label, id], i) => (
                <motion.button
                  key={id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.35 }}
                  data-testid={`mobile-nav-link-${label.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => go(id)}
                  className="flex w-full items-baseline gap-4 border-b border-white/5 py-4 text-left font-mono text-sm uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 last:border-b-0 hover:text-[#00FF41]"
                >
                  <span className="text-[11px] text-[#00FF41]">0{i + 1}</span>
                  {label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.35 }}
                data-testid="mobile-nav-join-pilot"
                onClick={() => go("#pilot")}
                className="mt-6 w-full bg-[#00FF41] px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
              >
                Join the pilot →
              </motion.button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
